/**
 * LocalStorage operations for LinCal data persistence
 */

import { getDefaultData, validateData, migrateData } from './dataDefaults';
import { STORAGE_KEY } from './constants';

/**
 * Load data from LocalStorage
 * @param {string} key - Storage key (defaults to STORAGE_KEY)
 * @param {string} personalProjectName - Name for personal project if creating default (defaults to 'Personal')
 * @returns {Object} Calendar data
 */
export function loadData(key = STORAGE_KEY, personalProjectName = 'Personal') {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) {
      return getDefaultData(personalProjectName);
    }
    
    const data = JSON.parse(stored);
    
    // Validate structure
    if (!validateData(data)) {
      console.warn('Invalid data structure, using defaults');
      return getDefaultData(personalProjectName);
    }
    
    // Run migrations (e.g., ensure holidays project exists)
    migrateData(data);
    
    return data;
  } catch (error) {
    console.error('Error loading data from LocalStorage:', error);
    return getDefaultData(personalProjectName);
  }
}

/**
 * Save data to LocalStorage
 * @param {Object} data - Calendar data to save
 * @param {string} key - Storage key (defaults to STORAGE_KEY)
 */
export function saveData(data, key = STORAGE_KEY) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving data to LocalStorage:', error);
    // Could be quota exceeded - throw error for caller to handle
    throw new Error('Failed to save data. LocalStorage might be full.');
  }
}

/**
 * Clear all data from LocalStorage
 * @param {string} key - Storage key (defaults to STORAGE_KEY)
 */
export function clearData(key = STORAGE_KEY) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing data:', error);
  }
}

/**
 * Check if there is existing calendar data in LocalStorage
 * @param {string} key - Storage key (defaults to STORAGE_KEY)
 * @returns {boolean} True if data exists
 */
export function hasExistingData(key = STORAGE_KEY) {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error('Error checking existing data:', error);
    return false;
  }
}
